import { useState, useEffect } from "react";
import { CheckCircle2, TrendingUp, AlertTriangle, Play, RefreshCw, BarChart2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";

interface WeightInfo {
  id: string;
  recommendation_type: string;
  context_tags: string[];
  weight: number;
  successful_runs: number;
  failed_runs: number;
  updated_at: string;
}

interface FeedbackLog {
  id: string;
  plan_id: string;
  incident_id: string;
  recommendation_id: string;
  action_taken: "execute" | "ignore" | "alternative";
  alternative_action_name: string | null;
  hesitation_ms: number;
  outcome: "resolved" | "failed" | "rolled_back";
  created_at: string;
}

export function LearningConfig() {
  const [weights, setWeights] = useState<WeightInfo[]>([]);
  const [logs, setLogs] = useState<FeedbackLog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLearningData = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const headers = { Authorization: `Bearer ${session?.access_token}` };

      const [wRes, fRes] = await Promise.all([
        fetch(`${apiUrl}/api/learning/weights`, { headers }),
        fetch(`${apiUrl}/api/learning/feedback`, { headers })
      ]);

      if (wRes.ok && fRes.ok) {
        setWeights(await wRes.json());
        setLogs(await fRes.json());
      }
    } catch (e) {
      console.error("Failed to load operational learning metadata", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLearningData();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-black uppercase tracking-tight">Operational Learning Weights</p>
          <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">
            Remediation plan recommendation score engines based on historical validation
          </p>
        </div>
        <button
          onClick={fetchLearningData}
          disabled={loading}
          className="h-8 px-3 rounded-none border-2 border-foreground bg-background font-black uppercase text-[9px] gap-1.5 flex items-center shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh Stats
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Recommendation Engine Weights */}
        <Card className="border-2 border-foreground rounded-none shadow-brutal-sm">
          <CardHeader className="border-b-2 border-foreground bg-accent/5 p-4">
            <CardTitle className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
              <TrendingUp className="h-4 w-4" /> Action Scoring Rankings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {weights.length === 0 ? (
              <p className="text-[10px] text-muted-foreground font-black uppercase py-4 text-center">
                No scoring metadata resolved yet
              </p>
            ) : (
              weights.map(w => (
                <div key={w.id} className="flex items-center justify-between border border-foreground/10 p-3 bg-background">
                  <div className="space-y-1">
                    <span className="text-[10px] font-black uppercase block">{w.recommendation_type}</span>
                    <span className="text-[8px] bg-primary/10 border border-primary/20 px-1 py-0.5 font-bold uppercase text-primary tracking-widest">
                      {w.context_tags.join(", ")}
                    </span>
                  </div>
                  <div className="flex gap-4 text-right">
                    <div>
                      <p className="text-[8px] font-bold uppercase text-muted-foreground">Successful Runs</p>
                      <p className="text-[10px] font-black text-status-up">{w.successful_runs}</p>
                    </div>
                    <div>
                      <p className="text-[8px] font-bold uppercase text-muted-foreground">Learning Weight</p>
                      <p className="text-[10px] font-black text-foreground">{Number(w.weight).toFixed(2)}x</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Action Decision Logs */}
        <Card className="border-2 border-foreground rounded-none shadow-brutal-sm">
          <CardHeader className="border-b-2 border-foreground bg-accent/5 p-4">
            <CardTitle className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
              <BarChart2 className="h-4 w-4" /> Operator Action Logs
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {logs.length === 0 ? (
              <p className="text-[10px] text-muted-foreground font-black uppercase py-4 text-center">
                No recent operator feedback archived
              </p>
            ) : (
              logs.map(log => (
                <div key={log.id} className="flex items-center justify-between border border-foreground/10 p-2.5 bg-background text-[9px] font-bold uppercase">
                  <div className="space-y-0.5">
                    <span className="font-black text-foreground block">
                      {log.action_taken === "execute" ? "Run Executed" : "Remediation Ignored"}
                    </span>
                    <span className="text-muted-foreground text-[8px]">
                      Hesitation: {(log.hesitation_ms / 1000).toFixed(1)}s | Plan: {log.plan_id.substring(0, 8)}
                    </span>
                  </div>
                  <div>
                    <span className={`px-2 py-0.5 border ${
                      log.outcome === "resolved"
                        ? "bg-status-up/10 border-status-up/30 text-status-up"
                        : "bg-status-down/10 border-status-down/30 text-status-down"
                    }`}>
                      {log.outcome}
                    </span>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
